import { useCallback, useEffect, useRef, useState } from 'react';
import { CHAVE_PADRAO, criarTranca } from './tranca';

/**
 * useAgir — o clique que não acontece duas vezes.
 *
 * Embrulha `criarTranca` (a lógica, testada em `tests/tranca.test.ts`) no que o
 * React precisa: um estado para o véu aparecer.
 *
 *   const { ocupado, agir } = useAgir();
 *   <Veu esperando={ocupado}> … </Veu>
 *   <button onClick={() => agir(async () => { await lancar(); })}>Lançar</button>
 *
 * Quando a tela tem várias ações independentes — estornar a linha 3 não pode
 * travar a linha 4 — cada uma leva a própria chave:
 *
 *   const { rodando, agir } = useAgir();
 *   agir(`estorno:${id}`, async () => { … })
 *   <Veu esperando={rodando(`estorno:${id}`)} rasa> … </Veu>
 *
 * `agir` devolve `undefined` quando o clique foi barrado, e o valor da função
 * quando passou — dá para distinguir.
 */
export function useAgir() {
  const tranca = useRef(criarTranca()).current;
  const [presas, setPresas] = useState<ReadonlySet<string>>(() => new Set());

  /*
   * ⚠️ `vivo.current = true` DENTRO do efeito, não na inicialização do ref.
   *
   * No StrictMode do React 19 o efeito roda monta → desmonta → monta. Se o
   * `true` só existisse na criação do ref, a primeira limpeza deixaria `false`
   * para sempre e a tela nunca mais sairia do "carregando".
   */
  const vivo = useRef(true);
  useEffect(() => {
    vivo.current = true;
    return () => {
      vivo.current = false;
      tranca.soltarTudo();
    };
  }, [tranca]);

  const sincronizar = useCallback(() => {
    if (vivo.current) setPresas(tranca.presas());
  }, [tranca]);

  function agir<T>(fn: () => Promise<T>): Promise<T | undefined>;
  function agir<T>(chave: string, fn: () => Promise<T>): Promise<T | undefined>;
  async function agir<T>(
    a: string | (() => Promise<T>),
    b?: () => Promise<T>,
  ): Promise<T | undefined> {
    const chave = typeof a === 'string' ? a : CHAVE_PADRAO;
    const fn = typeof a === 'string' ? b! : a;

    if (!tranca.pegar(chave)) return undefined;
    sincronizar();
    try {
      return await fn();
    } finally {
      /*
       * `finally`, e não depois do `await`: se a chamada estourar, a chave tem
       * que voltar mesmo assim — senão um erro de rede deixa o botão morto até
       * a lojista recarregar a página.
       */
      tranca.soltar(chave);
      sincronizar();
    }
  }

  const estavel = useRef(agir);
  estavel.current = agir;

  return {
    /** Qualquer ação rodando. */
    ocupado: presas.size > 0,
    /** Esta chave rodando. */
    rodando: useCallback((chave: string = CHAVE_PADRAO) => presas.has(chave), [presas]),
    /**
     * Identidade estável entre renderizações — pode entrar em `useEffect` e em
     * `useCallback` sem reexecutar tudo.
     */
    agir: useCallback(
      ((...args: unknown[]) =>
        (estavel.current as (...a: unknown[]) => Promise<unknown>)(...args)) as typeof agir,
      [],
    ),
  };
}
